import {
  DocumentIdRecord,
  useDocumentIds,
  useSiteList,
} from "@inrange/building-manager-api-client";
import { Icons } from "@inrange/theme-components/icons";
import { useMemo, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import DateRangePicker from "../../components/DatePickers/DateRangePicker";
import { WithTooltip } from "../../components/WithTooltip";
import CollapsibleCard from "../site/CollapsibleCard";
import {
  OPERATIONAL_STATUSES,
  STATUS_OPTIONS,
} from "../site/operationalStatusUtils";
import stringToReactNodesWithLinks from "../site/siteSections/stringToReactNodesWithLinks";

const formatDate = (d: Date) => d.toISOString().split("T")[0];

const statusLabel = (status: string) =>
  (STATUS_OPTIONS as Record<string, string>)[status] || status;

const sortKey = (name: string) => name.trim().replaceAll(" ", "").toLowerCase();

const DocumentNumbersCard = () => {
  const [docType, setDocType] = useState("INV");
  const now = new Date();
  const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: threeMonthsAgo,
    end: now,
  });

  const [selectedSite, setSelectedSite] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [createNote, setCreateNote] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const startDate = dateRange.start ? formatDate(dateRange.start) : "";
  const endDate = dateRange.end ? formatDate(dateRange.end) : "";

  const {
    fetchDocumentIds,
    fetchNextNumber,
    createDocumentId,
    removeDocumentId,
    voidDocumentId,
  } = useDocumentIds({
    type: docType,
    startDate,
    endDate,
  });

  const { fetchSites } = useSiteList();
  const siteSelectOptions = useMemo(() => {
    const sites = (fetchSites.data?.sites || [])
      .map((site: { id: string; name: string; operationalStatus: string }) => ({
        label: `${site.name} (${statusLabel(site.operationalStatus)})`,
        value: site.id,
        name: site.name,
        operational: OPERATIONAL_STATUSES.has(site.operationalStatus),
      }))
      .sort((a: { name: string }, b: { name: string }) =>
        sortKey(a.name).localeCompare(sortKey(b.name))
      );
    const operational = sites.filter((s) => s.operational);
    const other = sites.filter((s) => !s.operational);
    return [
      { label: "Operational", options: operational },
      { label: "Other", options: other },
    ];
  }, [fetchSites.data?.sites]);

  const siteNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of siteSelectOptions) {
      for (const opt of group.options) {
        map.set(opt.value, opt.name);
      }
    }
    return map;
  }, [siteSelectOptions]);

  const handleCreate = () => {
    setCreateError(null);
    createDocumentId.mutate(
      {
        type: docType,
        siteID: selectedSite?.value || undefined,
        note: createNote || undefined,
      },
      {
        onSuccess: () => {
          setSelectedSite(null);
          setCreateNote("");
        },
        onError: (err: unknown) => {
          const response = (err as { response?: { data?: { error?: string } } })
            ?.response;
          const message =
            response?.data?.error ||
            (err instanceof Error ? err.message : null) ||
            "Failed to claim document number";
          setCreateError(message);
        },
      }
    );
  };

  const siteName = (siteID: string | null) => {
    if (!siteID) return "-";
    return siteNameById.get(siteID) || siteID;
  };

  return (
    <CollapsibleCard title="Document Numbers" initialOpen={true}>
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <Form.Select
          size="sm"
          style={{ width: "120px" }}
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
        >
          <option value="INV">INV</option>
          <option value="QST">QST</option>
        </Form.Select>
        <span>
          Next available:{" "}
          <strong>
            {fetchNextNumber.isLoading
              ? "..."
              : fetchNextNumber.data?.formatted || "N/A"}
          </strong>
        </span>
      </div>

      <div className="d-flex align-items-end gap-3 mb-3 flex-wrap">
        <div style={{ minWidth: "400px" }}>
          <Form.Label style={{ fontSize: "0.875rem" }}>
            Site (optional)
          </Form.Label>
          <Select
            placeholder="Search sites..."
            value={selectedSite}
            onChange={(value) => setSelectedSite(value)}
            options={siteSelectOptions}
            isClearable
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                minHeight: 31,
                border: "var(--bs-border-width) solid var(--bs-border-color)",
                borderRadius: "var(--bs-border-radius)",
                color: "var(--bs-body-color)",
                fontSize: 14,
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
                padding: "0 6px",
              }),
              input: (baseStyles) => ({ ...baseStyles, margin: 0, padding: 0 }),
              dropdownIndicator: (base) => ({ ...base, padding: 4 }),
              clearIndicator: (base) => ({ ...base, padding: 4 }),
            }}
          />
        </div>
        <Form.Group style={{ minWidth: "350px", flex: 1 }}>
          <Form.Label style={{ fontSize: "0.875rem" }}>
            Note (optional)
          </Form.Label>
          <Form.Control
            type="text"
            size="sm"
            value={createNote}
            onChange={(e) => setCreateNote(e.target.value)}
            placeholder="e.g. Manual allocation"
          />
        </Form.Group>
      </div>
      {createError && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setCreateError(null)}
        >
          {createError}
        </Alert>
      )}
      <div>
        <Button
          variant="success"
          size="sm"
          onClick={handleCreate}
          disabled={createDocumentId.isLoading}
        >
          {createDocumentId.isLoading ? "Claiming..." : "Claim next available"}
        </Button>
      </div>

      <hr />

      <h6 className="fw-bold mb-3" style={{ fontSize: 14 }}>
        Existing document numbers
      </h6>

      <div className="mb-3">
        <DateRangePicker
          value={dateRange}
          onChange={setDateRange}
          mode="date"
          size="sm"
        />
      </div>

      {fetchDocumentIds.isLoading ? (
        <div>Loading...</div>
      ) : (
        <div style={{ maxHeight: "330px", overflowY: "auto" }}>
          <Table
            striped
            bordered
            hover
            size="sm"
            style={{ fontSize: 14, marginBottom: 0 }}
          >
            <thead
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "var(--bs-body-bg)",
                zIndex: 1,
              }}
            >
              <tr>
                <th>Number</th>
                <th>Created</th>
                <th>Site</th>
                <th>Key</th>
                <th>Note</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(fetchDocumentIds.data?.documentIds || []).map(
                (doc: DocumentIdRecord) => (
                  <tr
                    key={`${doc.type}-${doc.number}`}
                    style={{
                      textDecoration: doc.isVoided ? "line-through" : "none",
                      opacity: doc.isVoided ? 0.6 : 1,
                    }}
                  >
                    <td>
                      {doc.type}-{doc.number}
                    </td>
                    <td>{doc.createdAt}</td>
                    <td>
                      {doc.siteID ? (
                        <Link to={`/site/${doc.siteID}/filesreports/files`}>
                          {siteName(doc.siteID)}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{doc.key || "-"}</td>
                    <td>
                      {doc.note ? stringToReactNodesWithLinks(doc.note) : "-"}
                    </td>
                    <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      <WithTooltip
                        text={doc.isVoided ? "Reinstate" : "Void invoice"}
                      >
                        <img
                          alt={doc.isVoided ? "reinstate" : "void"}
                          src={
                            doc.isVoided ? Icons.circleTick : Icons.crossYellow
                          }
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                            verticalAlign: "middle",
                          }}
                          onClick={() =>
                            voidDocumentId.mutate({
                              type: doc.type,
                              number: doc.number,
                              isVoided: !doc.isVoided,
                            })
                          }
                        />
                      </WithTooltip>
                      {doc.manuallyCreated && (
                        <img
                          alt="delete"
                          src={Icons.trashcanRed}
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                            marginLeft: "8px",
                            verticalAlign: "middle",
                          }}
                          onClick={() =>
                            removeDocumentId.mutate({
                              type: doc.type,
                              number: doc.number,
                            })
                          }
                        />
                      )}
                    </td>
                  </tr>
                )
              )}
              {(fetchDocumentIds.data?.documentIds || []).length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}
    </CollapsibleCard>
  );
};

export default DocumentNumbersCard;
